import type { GridColDef } from '@mui/x-data-grid';
import type { CustomerOverview } from '@vy/api-definition';
import { getSelectorAttribute, customersOverviewPageSelectors } from '@vy/app-selectors';
import { CUSTOMERS_COLUMNS_KEYS } from '@vy/customers-types';
import { INVOICE_DUE_DATE_VALUES } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import { CustomersDotMenu } from '../customers-page-components/CustomersDotMenu';
import { CustomerLabelsCell } from '../customers-page-components/CustomerLabelsCell';
import { CustomerStatusSelect } from '../customers-page-components/CustomerStatusSelect/CustomerStatusSelect';
import { formatDate } from '../../../../utils/date';
import { getCustomerTaxTypeLabel } from '../customers-page-utils/get-customer-tax-type-label';
import { VyTooltip } from '../../../../components/new-design/VyTooltip';

export const createCustomersColumns = (
  defaultSubsidiary: string | undefined,
): GridColDef<CustomerOverview>[] => [
  {
    field: CUSTOMERS_COLUMNS_KEYS.STATUS,
    headerName: 'Status',
    description: 'Status',
    minWidth: 160,
    flex: 1,
    headerClassName: 'pl-6',
    cellClassName: 'pl-6',
    renderCell: ({ row }) => (
      <span {...getSelectorAttribute(customersOverviewPageSelectors.columns.status)}>
        <CustomerStatusSelect value={row.status} customer={row} />
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.NAME,
    headerName: 'Customer',
    description: 'Customer',
    minWidth: 250,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black overflow-hidden text-ellipsis whitespace-nowrap block"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.name)}
      >
        {row.name}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.LEGAL_NAME,
    headerName: 'Legal Name',
    description: 'Legal Name',
    minWidth: 250,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black overflow-hidden text-ellipsis whitespace-nowrap block"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.legalName)}
      >
        {row.legalName ?? '--'}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.LABELS,
    headerName: 'Label',
    description: 'Label',
    minWidth: 200,
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => (
      <span {...getSelectorAttribute(customersOverviewPageSelectors.columns.labels)}>
        <CustomerLabelsCell customer={row} />
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.CRM_ID,
    headerName: 'CRM ID',
    description: 'CRM ID',
    minWidth: 150,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black overflow-hidden text-ellipsis whitespace-nowrap block"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.crmId)}
      >
        {row.crmId ?? '--'}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.ERP_ID,
    headerName: 'ERP ID',
    description: 'ERP ID',
    minWidth: 150,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black overflow-hidden text-ellipsis whitespace-nowrap block"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.erpId)}
      >
        {row.erpId ?? '--'}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.TAX_ID,
    headerName: 'Tax ID',
    description: 'Tax ID',
    minWidth: 150,
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => {
      const taxIds = (row.taxIds ?? []).filter(Boolean);
      const text = taxIds.join(', ');

      return (
        <VyTooltip tooltipContent={text || null}>
          <span
            className="font-sans text-black overflow-hidden text-ellipsis whitespace-nowrap block"
            {...getSelectorAttribute(customersOverviewPageSelectors.columns.taxId)}
          >
            {text || '--'}
          </span>
        </VyTooltip>
      );
    },
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.TAX_TYPE,
    headerName: 'Tax Type',
    description: 'Tax Type',
    minWidth: 150,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black overflow-hidden text-ellipsis whitespace-nowrap block"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.taxType)}
      >
        {getCustomerTaxTypeLabel(row.taxRate)}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.WALLET,
    headerName: 'Wallet',
    description: 'Wallet',
    minWidth: 150,
    flex: 1,
    renderCell: ({ row }) => (
      <VyTooltip tooltipContent={row.wallet || null}>
        <span
          className="font-sans text-black overflow-hidden text-ellipsis whitespace-nowrap block"
          {...getSelectorAttribute(customersOverviewPageSelectors.columns.wallet)}
        >
          {row.wallet ?? '--'}
        </span>
      </VyTooltip>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.ALIASES,
    headerName: 'Aliases',
    description: 'Aliases',
    minWidth: 200,
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => {
      const aliases = (row.aliases ?? []).filter(Boolean);
      const text = aliases.join(', ');

      return (
        <VyTooltip tooltipContent={text || null}>
          <span
            className="font-sans text-black overflow-hidden text-ellipsis whitespace-nowrap block"
            {...getSelectorAttribute(customersOverviewPageSelectors.columns.aliases)}
          >
            {text || '--'}
          </span>
        </VyTooltip>
      );
    },
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.ACCRUED,
    headerName: 'Contracts Assigned',
    description: 'Contracts Assigned',
    minWidth: 115,
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.contractsCount)}
      >
        {row.contractsCount}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.SUBSIDIARY,
    headerName: 'Subsidiary',
    description: 'Subsidiary',
    minWidth: 115,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.subsidiary)}
      >
        {row.subsidiary ?? defaultSubsidiary ?? '--'}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.CREATED_AT,
    headerName: 'Created Date',
    description: 'Created Date',
    minWidth: 115,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.createdAt)}
      >
        {formatDate(row.createdAt)}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.UPDATED_AT,
    headerName: 'Updated Date',
    description: 'Updated Date',
    minWidth: 115,
    flex: 1,
    renderCell: ({ row }) => <span className="font-sans text-black">{formatDate(row.updatedAt)}</span>,
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.ADDRESS,
    headerName: 'Address',
    description: 'Address',
    minWidth: 300,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black overflow-hidden text-ellipsis whitespace-nowrap block"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.address)}
      >
        {row.address ?? '--'}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.COUNTRY,
    headerName: 'Country',
    description: 'Country',
    minWidth: 115,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.country)}
      >
        {row.country ?? '--'}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.STATE,
    headerName: 'State',
    description: 'State',
    minWidth: 115,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.state)}
      >
        {row.state ?? '--'}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.CITY,
    headerName: 'City',
    description: 'City',
    minWidth: 115,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.city)}
      >
        {row.city ?? '--'}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.POSTAL_CODE,
    headerName: 'Postal Code',
    description: 'Postal Code',
    minWidth: 115,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.postalCode)}
      >
        {row.postalCode ?? '--'}
      </span>
    ),
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.PAYMENT_DUE_DAYS,
    headerName: 'Invoice Due Date',
    description: 'Invoice Due Date',
    minWidth: 115,
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => {
      const displayValue =
        row.paymentDueDays === INVOICE_DUE_DATE_VALUES.EOM ? 'EOM' : (row.paymentDueDays ?? '--');

      return (
        <span
          className="font-sans text-black"
          {...getSelectorAttribute(customersOverviewPageSelectors.columns.paymentDueDays)}
        >
          {displayValue}
        </span>
      );
    },
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.MANUAL_REVIEW,
    headerName: 'Manual Review - On/Off',
    description: 'Manual Review - On/Off',
    minWidth: 145,
    flex: 1,
    renderCell: ({ row }) => {
      const manualReview = isNotNullOrUndefined(row.manualReview) ? (row.manualReview ? 'On' : 'Off') : '--';

      return (
        <span
          className="font-sans text-black"
          {...getSelectorAttribute(customersOverviewPageSelectors.columns.manualReview)}
        >
          {manualReview}
        </span>
      );
    },
  },
  {
    field: CUSTOMERS_COLUMNS_KEYS.PARENT_CUSTOMER,
    headerName: 'Parent Customer',
    description: 'Parent Customer',
    minWidth: 145,
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black"
        {...getSelectorAttribute(customersOverviewPageSelectors.columns.parentCustomer)}
      >
        {row.parentCustomer ?? '--'}
      </span>
    ),
  },
  {
    field: 'actionMenu',
    headerName: '',
    width: 10,
    disableExport: true,
    sortable: false,
    headerClassName: 'bg-white',
    renderCell: ({ row: { id, contractsCount, name } }) => (
      <CustomersDotMenu customerId={id} customerName={name} contractsCount={contractsCount} />
    ),
  },
];
